import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import {
  ArrowDownTrayIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import {
  Avatar,
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@heroui/react";
import AgreementSignPanel from "../../../components/agreement/agreementSignPanel";
import AgreementPdfViewer from "../../../components/agreement/agreementPdfViewer";
import {
  DealActivityResponseDto,
  DealAgreementResponseDto,
  DealResponseDto,
} from "../../../types/types";
import {
  canAcceptDeal,
  canSignAgreement,
  getAgreementMessageKey,
} from "../../../utils/agreementHelpers";
import type { TranslationFn } from "../createDeal/dealFormFields";
import { asRecord, isRecord } from "../../../utils/typeGuards";
import { escapeHtml, sanitizeHtml } from "../../../utils/safeHtml";

type StepKey =
  | "PROPOSED"
  | "AGREEMENT"
  | "REQUIRED_DOCUMENTS"
  | "ACCEPTED"
  | "CONFIRMED"
  | "WAITING_FOR_RESULT"
  | "RESULT_UPDATED"
  | "RESULT_CONFIRMED"
  | "POST_PAYMENT"
  | "PRE_PAYMENT_RELEASED"
  | "POST_PAYMENT_RELEASED"
  | "FIGHTER_RATED"
  | "MANAGER_RATED"
  | "FINISHED"
  | "PAYMENT_REFUNDED"
  | "EXPIRED"
  | "DISPUTE"
  | "CANCELLED"
  | "REJECTED";

type DocumentReviewStatus = "APPROVED" | "REJECTED";

interface RequiredDocumentItem {
  documentType: string;
  notes?: string;
  deadlineDate?: string;
  uploadStatus?: string;
  uploadedFile?: string;
  reviewComment?: string;
}

interface ActionButtonConfig {
  labelKey: string;
  tab: string;
  scrollTargetId?: string;
}

interface DealActivity extends DealActivityResponseDto {
  i18nKey?: string;
  messageArgs?: unknown;
  pendingMessageArgs?: unknown;
}

interface DealActivityTabProps {
  deal: DealResponseDto;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  t: TranslationFn;
  formData?: Record<string, unknown> | null;
  renderDynamicObject?: (
    objectData: unknown,
    skip?: number,
  ) => React.ReactNode;
  currentUserRole?: "MANAGER" | "FIGHTER" | string;
  tr: (value?: string) => string;
  agreement?: DealAgreementResponseDto | null;
  onSignAgreement?: (signatureDataUrl: string) => Promise<void>;
  onAcceptDeal?: () => Promise<void>;
  onDeclineDeal?: () => Promise<void>;
  isDealActionProcessing?: boolean;
  onUploadRequiredDocument?: (
    documentType: string,
    file: File,
  ) => Promise<void>;
  onReviewRequiredDocument?: (
    documentType: string,
    status: DocumentReviewStatus,
    reviewComment?: string,
  ) => Promise<void>;
}

const getStepVisualConfig = (params: {
  isTerminalFailure: boolean;
  isDispute: boolean;
  completed: boolean;
  current: boolean;
  isFuture: boolean;
  isDocumentStep?: boolean;
}) => {
  const { isTerminalFailure, isDispute, completed, current, isFuture, isDocumentStep } = params;

  if (isTerminalFailure) {
    return {
      circleBg: "bg-[#FECDD3]",
      icon: <XCircleIcon className="w-5 h-5 text-[#DC2626]" />,
      labelColor: "text-[#DC2626]",
      lineColor: "bg-red-200",
    };
  }

  if (isDispute) {
    return {
      circleBg: "bg-[#FEF08A]",
      icon: <ExclamationTriangleIcon className="w-5 h-5 text-[#CA8A04]" />,
      labelColor: "text-[#CA8A04]",
      lineColor: "bg-amber-200",
    };
  }

  if (completed) {
    return {
      circleBg: "bg-[#A7F3D0]",
      icon: <CheckCircleIcon className="w-5 h-5 text-[#15803D]" />,
      labelColor: "text-[#16A34A]",
      lineColor: "bg-emerald-300",
    };
  }

  if (current) {
    return {
      circleBg: "bg-[#FED7AA]",
      icon: <ClockIcon className="w-5 h-5 text-[#EA580C]" />,
      labelColor: "text-[#EA580C]",
      lineColor: "bg-orange-200",
    };
  }

  if (isDocumentStep) {
    return {
      circleBg: "bg-gray-100",
      icon: <DocumentTextIcon className="w-5 h-5 text-gray-500" />,
      labelColor: "text-gray-500",
      lineColor: "bg-gray-200",
    };
  }

  return {
    circleBg: "bg-gray-100",
    icon: <CheckCircleIcon className="w-5 h-5 text-gray-300" />,
    labelColor: isFuture ? "text-gray-400" : "text-gray-600",
    lineColor: "bg-gray-200",
  };
};

const statusLabels: Record<StepKey, string> = {
  PROPOSED: "progressStatusProposed",
  AGREEMENT: "progressStatusAgreement",
  REQUIRED_DOCUMENTS: "progressStatusRequiredDocuments",
  ACCEPTED: "progressStatusAccepted",
  CONFIRMED: "progressStatusPaymentConfirmed",
  WAITING_FOR_RESULT: "progressStatusWaitingForResult",
  RESULT_UPDATED: "progressStatusResultUpdated",
  FIGHTER_RATED: "progressStatusFighterRated",
  RESULT_CONFIRMED: "progressStatusResultConfirmed",
  MANAGER_RATED: "progressStatusManagerRated",
  FINISHED: "progressStatusFinished",
  DISPUTE: "progressStatusDisputed",
  CANCELLED: "progressStatusCancelled",
  REJECTED: "progressStatusRejected",
  PRE_PAYMENT_RELEASED: "progressStatusPrePaymentReleased",
  POST_PAYMENT: "progressStatusPostPayment",
  POST_PAYMENT_RELEASED: "progressStatusPostPaymentReleased",
  PAYMENT_REFUNDED: "progressStatusPaymentRefunded",
  EXPIRED: "progressStatusExpired",
};

const getStatusLabel = (step: StepKey, t: TranslationFn) => t(statusLabels[step]);

const getActivityStatusLabel = (status: string | undefined, t: TranslationFn) => {
  if (status && status in statusLabels) {
    return getStatusLabel(status as StepKey, t);
  }

  return (status ?? "").replace(/_/g, " ");
};

const normalizeDocumentStatus = (status?: string) =>
  String(status || "PENDING").trim().toUpperCase();

const formatDocumentName = (value?: string) => {
  const text = String(value || "Document")
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return "Document";

  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getFileNameFromUrl = (url?: string) => {
  if (!url) return "document.pdf";

  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const fileName = pathname.split("/").pop();
    return fileName ? decodeURIComponent(fileName) : "document.pdf";
  } catch {
    const fileName = url.split("?")[0].split("/").pop();
    return fileName || "document.pdf";
  }
};

const PdfFileBadge = ({
  fileName,
  className = "",
}: {
  fileName: string;
  className?: string;
}) => (
  <div
    className={`flex min-w-0 items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 ${className}`}
  >
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600">
      <DocumentIcon className="h-4 w-4" />
    </span>
    <span className="min-w-0 flex-1 truncate font-semibold" title={fileName}>
      {fileName}
    </span>
    <span className="shrink-0 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-4 text-red-700">
      PDF
    </span>
  </div>
);

const isPastDeadline = (deadlineDate?: string) => {
  if (!deadlineDate) return false;

  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  const deadline = dateOnlyPattern.test(deadlineDate)
    ? new Date(`${deadlineDate}T23:59:59`)
    : new Date(deadlineDate);

  if (Number.isNaN(deadline.getTime())) return false;
  return deadline.getTime() < Date.now();
};

const getDocumentStatusText = (status: string | undefined, t: TranslationFn) => {
  const normalizedStatus = normalizeDocumentStatus(status);

  switch (normalizedStatus) {
    case "APPROVED":
      return t("documentStatusApproved");
    case "REJECTED":
      return t("documentStatusRejected");
    case "PENDING_REVIEW":
      return t("documentStatusPendingReview");
    case "UPLOADED":
      return t("uploaded");
    case "MISSED":
      return t("deadlineMissed");
    default:
      return t("pendingUpload");
  }
};

const getDocumentStatusClasses = (status?: string) => {
  const normalizedStatus = normalizeDocumentStatus(status);

  if (normalizedStatus === "APPROVED") {
    return "border-green-200 bg-green-50 text-green-700";
  }

  if (normalizedStatus === "REJECTED" || normalizedStatus === "MISSED") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (
    normalizedStatus === "PENDING_REVIEW" ||
    normalizedStatus === "UPLOADED"
  ) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  return "border-gray-200 bg-gray-100 text-gray-600";
};

const getActivityActionButton = (
  activity: DealActivity,
  currentUserRole: "MANAGER" | "FIGHTER" | string | undefined,
  allActivities: DealActivity[] = [],
): ActionButtonConfig | null => {
  const status: string = activity.status ?? "";
  const skipProcessedCheck = status === "WAITING_FOR_RESULT";

  if (activity.processed && !skipProcessedCheck) return null;

  switch (status) {
    case "CONFIRMED":
      if (currentUserRole === "MANAGER") {
        return {
          labelKey: "progressActionConfirmPayment",
          tab: "componentPayments",
        };
      }
      break;

    case "WAITING_FOR_RESULT": {
      const resultAlreadyUpdated = allActivities.some(
        (item) => item.status === "RESULT_UPDATED",
      );

      if (resultAlreadyUpdated) return null;

      if (currentUserRole === "MANAGER") {
        return {
          labelKey: "progressActionUpdateResult",
          tab: "result",
        };
      }
      break;
    }

    case "RESULT_UPDATED":
      if (currentUserRole === "MANAGER") {
        return {
          labelKey: "progressActionUpdateResult",
          tab: "result",
        };
      }
      break;

    case "RESULT_CONFIRMED":
      if (currentUserRole === "FIGHTER") {
        return {
          labelKey: "progressActionConfirmResult",
          tab: "result",
        };
      }
      break;

    case "FIGHTER_RATED":
      if (currentUserRole === "FIGHTER") {
        return {
          labelKey: "progressActionSubmitRating",
          tab: "result",
        };
      }
      break;

    case "MANAGER_RATED":
      if (currentUserRole === "MANAGER") {
        return {
          labelKey: "progressActionSubmitRating",
          tab: "result",
        };
      }
      break;

    case "PRE_PAYMENT_RELEASED":
      if (currentUserRole === "MANAGER") {
        return {
          labelKey: "progressActionReleasePayment",
          tab: "componentPayments",
        };
      }
      break;

    case "POST_PAYMENT":
      if (currentUserRole === "MANAGER") {
        return {
          labelKey: "progressActionProcessPostPayment",
          tab: "componentPayments",
        };
      }
      break;

    default:
      break;
  }

  return null;
};

const getRequiredDocuments = (
  deal: DealResponseDto,
  formData?: Record<string, unknown> | null,
): RequiredDocumentItem[] => {
  const rawDocuments =
    asRecord(asRecord(deal).formData).documentRequirement ??
    formData?.documentRequirement;

  if (!Array.isArray(rawDocuments)) return [];

  return rawDocuments
    .filter(isRecord)
    .map((item) => ({
      documentType: String(item.documentType || ""),
      notes: typeof item.notes === "string" ? item.notes : undefined,
      deadlineDate:
        typeof item.deadlineDate === "string" ? item.deadlineDate : undefined,
      uploadStatus:
        typeof item.uploadStatus === "string" ? item.uploadStatus : undefined,
      uploadedFile:
        typeof item.uploadedFile === "string" ? item.uploadedFile : undefined,
      reviewComment:
        typeof item.reviewComment === "string"
          ? item.reviewComment
          : undefined,
    }))
    .filter((item) => item.documentType.trim() !== "");
};

const DealActivityTab: React.FC<DealActivityTabProps> = ({
  deal,
  activeTab,
  setActiveTab,
  t,
  formData,
  renderDynamicObject,
  currentUserRole,
  tr,
  agreement,
  onSignAgreement,
  onAcceptDeal,
  onDeclineDeal,
  isDealActionProcessing = false,
  onUploadRequiredDocument,
  onReviewRequiredDocument,
}) => {
  const navigate = useNavigate();

  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File | null>>(
    {},
  );
  const [documentErrors, setDocumentErrors] = useState<Record<string, string>>(
    {},
  );
  const [uploadingDocumentKey, setUploadingDocumentKey] = useState<string | null>(
    null,
  );
  const [reviewingDocumentKey, setReviewingDocumentKey] = useState<string | null>(
    null,
  );
  const [rejectingDocumentKey, setRejectingDocumentKey] = useState<string | null>(
    null,
  );
  const [reviewComments, setReviewComments] = useState<Record<string, string>>({});
  const [downloadingDocumentKey, setDownloadingDocumentKey] = useState<
    string | null
  >(null);


  const shouldDisableProgressActions = deal.status === "CANCELLED";
  const shouldHideProgressActions = deal.status === "FINISHED";

  const requiredDocuments = getRequiredDocuments(deal, formData);

  const hasRequiredDocuments = requiredDocuments.length > 0;

  const approvedDocumentCount = requiredDocuments.filter(
    (item) => normalizeDocumentStatus(item.uploadStatus) === "APPROVED",
  ).length;

  const hasDocumentsWaitingForUpload = requiredDocuments.some((item) => {
    const status = normalizeDocumentStatus(item.uploadStatus);
    return status === "PENDING" || status === "REJECTED";
  });

  const hasDocumentsPendingReview = requiredDocuments.some(
    (item) => normalizeDocumentStatus(item.uploadStatus) === "PENDING_REVIEW",
  );

  const allRequiredDocumentsApproved =
    hasRequiredDocuments && approvedDocumentCount === requiredDocuments.length;

  const documentsReadyForAcceptance =
    !hasRequiredDocuments || allRequiredDocumentsApproved;

  const canRespondToDeal =
    currentUserRole === "FIGHTER" &&
    deal.status === "PROPOSED" &&
    (!!onAcceptDeal || !!onDeclineDeal);

  const getActivityDisplayMessage = (activity: DealActivity) => {
    if (activity.message) {
      return sanitizeHtml(tr(activity.message));
    }

    let args: string[] = [];
    const rawArgs = activity.processed
      ? activity.messageArgs
      : activity.pendingMessageArgs;

    if (typeof rawArgs === "string" && rawArgs.trim()) {
      try {
        args = JSON.parse(rawArgs);
      } catch {
        args = [];
      }
    } else if (Array.isArray(rawArgs)) {
      args = rawArgs;
    }

    let key = activity.i18nKey || activity.status;
    if (
      !activity.processed &&
      key &&
      key !== "PROPOSED" &&
      !key.endsWith("_PENDING")
    ) {
      key = `${key}_PENDING`;
    }

    const translated = t(`activity_${key}`, {
      0: escapeHtml(args[0]),
      1: escapeHtml(args[1]),
      2: escapeHtml(args[2]),
      3: escapeHtml(args[3]),
      defaultValue: "",
    });

    if (translated) return sanitizeHtml(translated);
    return key ? escapeHtml(key.replace(/_/g, " ")) : "";
  };

  const shouldSwitchToComponentPayments = (activity: DealActivity) =>
    activity.status === "CONFIRMED" && currentUserRole === "MANAGER";

  const switchTabAndScroll = (tab: string, targetId?: string) => {
    setActiveTab(tab);
    if (!targetId) return;

    window.setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);
  };

  const isAgreementFullySigned =
    agreement?.status === "FULLY_SIGNED" || deal.status !== "PROPOSED";

  const getProgressSteps = (): StepKey[] => {
    const status = deal.status as StepKey | undefined;
    const activityStatuses = new Set<string>(
      (deal.activityResponse || []).map((item) => item.status),
    );

    const hasPostPayment = activityStatuses.has("POST_PAYMENT");
    const hasPostPaymentReleased = activityStatuses.has(
      "POST_PAYMENT_RELEASED",
    );
    const hasPaymentRefunded = activityStatuses.has("PAYMENT_REFUNDED");
    const hasPayablePostPayment = (deal.componentPayments || []).some(
      (payment) => {
        const paymentTiming = String(payment?.paymentTiming || "").toLowerCase();
        const paymentStatus = String(payment?.status || "").toUpperCase();
        const amount = Number(payment?.amount || 0);

        if (paymentTiming !== "post_event") return false;
        if (amount <= 0) return false;
        if (paymentStatus === "CANCELLED" || paymentStatus === "REFUNDED") {
          return false;
        }

        return true;
      },
    );

    const hasPostPaymentFlow =
      hasPostPayment || hasPostPaymentReleased || hasPayablePostPayment;

    const initialSteps: StepKey[] = ["PROPOSED", "AGREEMENT"];

    if (hasRequiredDocuments && isAgreementFullySigned) {
      initialSteps.push("REQUIRED_DOCUMENTS");
    }

    initialSteps.push(
      "ACCEPTED",
      "CONFIRMED",
      "WAITING_FOR_RESULT",
      "RESULT_UPDATED",
    );

    if (hasPostPaymentFlow) {
      initialSteps.push("RESULT_CONFIRMED");
      if (hasPostPayment || hasPayablePostPayment) {
        initialSteps.push("POST_PAYMENT");
      }
      if (hasPostPaymentReleased) {
        initialSteps.push("POST_PAYMENT_RELEASED");
      }
      initialSteps.push("MANAGER_RATED", "FIGHTER_RATED");
    } else {
      initialSteps.push(
        "MANAGER_RATED",
        "RESULT_CONFIRMED",
        "FIGHTER_RATED",
      );
    }

    initialSteps.push("PRE_PAYMENT_RELEASED");
    if (hasPaymentRefunded) initialSteps.push("PAYMENT_REFUNDED");
    initialSteps.push("FINISHED");

    if (status === "REJECTED") {
  return hasRequiredDocuments
    ? ["PROPOSED", "REQUIRED_DOCUMENTS", "REJECTED"]
    : ["PROPOSED", "REJECTED"];
}
    if (status === "CANCELLED") {
      const hasAccepted = (deal.activityResponse || []).some(
        (item) => item.status === "ACCEPTED",
      );

      if (hasAccepted) {
        return hasRequiredDocuments
          ? ["PROPOSED", "AGREEMENT", "REQUIRED_DOCUMENTS", "ACCEPTED", "CANCELLED"]
          : ["PROPOSED", "AGREEMENT", "ACCEPTED", "CANCELLED"];
      }

      return hasRequiredDocuments
        ? ["PROPOSED", "AGREEMENT", "REQUIRED_DOCUMENTS", "CANCELLED"]
        : ["PROPOSED", "AGREEMENT", "CANCELLED"];
    }

    if (status === "DISPUTE") {
      const disputeSteps: StepKey[] = ["PROPOSED", "AGREEMENT"];
      if (hasRequiredDocuments) disputeSteps.push("REQUIRED_DOCUMENTS");
      disputeSteps.push(
        "ACCEPTED",
        "CONFIRMED",
        "WAITING_FOR_RESULT",
        "RESULT_UPDATED",
        "DISPUTE",
      );
      return disputeSteps;
    }

    return initialSteps;
  };

  const progressSteps = getProgressSteps();

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentKey: string,
  ) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setSelectedFiles((previous) => ({
        ...previous,
        [documentKey]: null,
      }));
      return;
    }

    const isPdf =
      file.type === "application/pdf" || /\.pdf$/i.test(file.name);

    if (!isPdf) {
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]: t("onlyPdfAllowed"),
      }));
      setSelectedFiles((previous) => ({
        ...previous,
        [documentKey]: null,
      }));
      event.target.value = "";
      return;
    }

    setDocumentErrors((previous) => ({
      ...previous,
      [documentKey]: "",
    }));
    setSelectedFiles((previous) => ({
      ...previous,
      [documentKey]: file,
    }));
  };

  const handleUploadDocument = async (
    document: RequiredDocumentItem,
    documentKey: string,
  ) => {
    const file = selectedFiles[documentKey];

    if (!file) {
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]: t("pleaseChooseFileFirst"),
      }));
      return;
    }

    if (!onUploadRequiredDocument) {
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]: t("documentUploadUnavailable"),
      }));
      return;
    }

    setUploadingDocumentKey(documentKey);
    setDocumentErrors((previous) => ({
      ...previous,
      [documentKey]: "",
    }));

    try {
      await onUploadRequiredDocument(document.documentType, file);
      setSelectedFiles((previous) => ({
        ...previous,
        [documentKey]: null,
      }));
    } catch (error) {
      const errorRecord = asRecord(error);
      const responseData = asRecord(asRecord(errorRecord.response).data);
      const serverMessage =
        typeof responseData.message === "string" ? responseData.message : "";
      const errorMessage =
        typeof errorRecord.message === "string" ? errorRecord.message : "";
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]:
          serverMessage || errorMessage || t("unableToUploadDocument"),
      }));
    } finally {
      setUploadingDocumentKey(null);
    }
  };

  const handleReviewDocument = async (
    document: RequiredDocumentItem,
    documentKey: string,
    status: DocumentReviewStatus,
    reviewComment?: string,
  ) => {
    if (!onReviewRequiredDocument) {
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]: t("documentReviewUnavailable"),
      }));
      return;
    }

    setReviewingDocumentKey(documentKey);
    setDocumentErrors((previous) => ({
      ...previous,
      [documentKey]: "",
    }));

    try {
      await onReviewRequiredDocument(
        document.documentType,
        status,
        reviewComment?.trim(),
      );

      setRejectingDocumentKey(null);
      setReviewComments((previous) => ({
        ...previous,
        [documentKey]: "",
      }));
    } catch (error) {
      const errorRecord = asRecord(error);
      const responseData = asRecord(asRecord(errorRecord.response).data);
      const serverMessage =
        typeof responseData.message === "string" ? responseData.message : "";
      const errorMessage =
        typeof errorRecord.message === "string" ? errorRecord.message : "";
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]:
          serverMessage || errorMessage || t("unableToReviewDocument"),
      }));
    } finally {
      setReviewingDocumentKey(null);
    }
  };

  const handleDownloadDocument = async (
    requiredDocument: RequiredDocumentItem,
    documentKey: string,
  ) => {
    if (!requiredDocument.uploadedFile) return;

    setDownloadingDocumentKey(documentKey);
    setDocumentErrors((previous) => ({
      ...previous,
      [documentKey]: "",
    }));

    const fileName = getFileNameFromUrl(requiredDocument.uploadedFile);

    try {
      const response = await fetch(requiredDocument.uploadedFile);
      if (!response.ok) {
        throw new Error(t("downloadFailed"));
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = downloadUrl;
      anchor.download = fileName;
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    } catch (error) {
      const errorRecord = asRecord(error);
      const errorMessage =
        typeof errorRecord.message === "string" ? errorRecord.message : "";
      setDocumentErrors((previous) => ({
        ...previous,
        [documentKey]: errorMessage || t("unableToDownloadDocument"),
      }));
    } finally {
      setDownloadingDocumentKey(null);
    }
  };

  const renderManagerDocumentCard = (
    document: RequiredDocumentItem,
    index: number,
  ) => {
    const documentKey = `manager-document-${index}`;
    const status = normalizeDocumentStatus(document.uploadStatus);
    const documentName = document.documentType;
    const hasUploadedFile = Boolean(document.uploadedFile);
    const canReview =
      deal.status === "PROPOSED" &&
      status === "PENDING_REVIEW" &&
      hasUploadedFile;
    const isRejecting = rejectingDocumentKey === documentKey;
    const isReviewing = reviewingDocumentKey === documentKey;
    const isDownloading = downloadingDocumentKey === documentKey;

    return (
      <div
        key={documentKey}
        className="rounded-lg border border-gray-200 bg-white p-4"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              {formatDocumentName(documentName)}
            </p>

            {document.deadlineDate && (
              <p className="mt-1 text-xs text-gray-500">
                {t("documentDeadline", { date: document.deadlineDate })}
              </p>
            )}

            {document.notes && (
              <p className="mt-1 text-xs leading-5 text-gray-500">
                {document.notes}
              </p>
            )}

            {hasUploadedFile ? (
              <p className="mt-1 break-all text-xs text-gray-500">
                {getFileNameFromUrl(document.uploadedFile)}
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                {t("waitingForFighterDocumentUpload")}
              </p>
            )}
          </div>

          <span
            className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-xs font-medium ${getDocumentStatusClasses(
              status,
            )}`}
          >
            {getDocumentStatusText(status, t)}
          </span>
        </div>

        {status === "REJECTED" && document.reviewComment && (
          <div className="mt-3 rounded-md border border-red-100 bg-red-50 p-3">
            <p className="text-xs font-medium text-red-700">
              {t("rejectionReason")}
            </p>
            <p className="mt-1 text-sm text-red-700">
              {document.reviewComment}
            </p>
          </div>
        )}

        {hasUploadedFile && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleDownloadDocument(document, documentKey)}
              disabled={isDownloading}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isDownloading ? t("downloading") : t("download")}
            </button>

            {canReview && !isRejecting && (
              <>
                <button
                  type="button"
                  onClick={() => setRejectingDocumentKey(documentKey)}
                  disabled={isReviewing || !onReviewRequiredDocument}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                >
                  <XMarkIcon className="h-4 w-4" />
                  {t("reject")}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    void handleReviewDocument(
                      document,
                      documentKey,
                      "APPROVED",
                    )
                  }
                  disabled={isReviewing || !onReviewRequiredDocument}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-green-600 px-3 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                >
                  <CheckIcon className="h-4 w-4" />
                  {isReviewing ? t("processing") : t("approve")}
                </button>
              </>
            )}
          </div>
        )}

        {canReview && isRejecting && (
          <div className="mt-4 rounded-lg border border-red-100 bg-red-50 p-3">
            <label className="text-sm font-medium text-gray-800">
              {t("reasonForRejection")}
            </label>
            <textarea
              value={reviewComments[documentKey] || ""}
              onChange={(event) => {
                setReviewComments((previous) => ({
                  ...previous,
                  [documentKey]: event.target.value,
                }));
                setDocumentErrors((previous) => ({
                  ...previous,
                  [documentKey]: "",
                }));
              }}
              maxLength={1000}
              rows={3}
              placeholder={t("enterRejectionReason")}
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-gray-500"
            />

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectingDocumentKey(null);
                  setDocumentErrors((previous) => ({
                    ...previous,
                    [documentKey]: "",
                  }));
                }}
                disabled={isReviewing}
                className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("cancel")}
              </button>

              <button
                type="button"
                onClick={() =>
                  void handleReviewDocument(
                    document,
                    documentKey,
                    "REJECTED",
                    reviewComments[documentKey],
                  )
                }
                disabled={isReviewing || !onReviewRequiredDocument}
                className="h-9 rounded-md bg-red-600 px-3 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
              >
                {isReviewing ? t("processing") : t("confirmReject")}
              </button>
            </div>
          </div>
        )}

        {documentErrors[documentKey] && (
          <p className="mt-2 text-xs font-medium text-red-600">
            {documentErrors[documentKey]}
          </p>
        )}
      </div>
    );
  };

  const renderRequiredDocumentsStep = (index: number) => {
    const isLastStep = index === progressSteps.length - 1;
    const completed = allRequiredDocumentsApproved;
    const visual = getStepVisualConfig({
      isTerminalFailure: false,
      isDispute: false,
      completed,
      current: !completed,
      isFuture: false,
      isDocumentStep: true,
    });

    let summary = t("requiredDocumentsMustBeCompleted");

    if (completed) {
      summary = t("allRequiredDocumentsApproved");
    } else if (currentUserRole === "MANAGER" && hasDocumentsPendingReview) {
      summary = t("fighterUploadedDocumentsReview");
    } else if (currentUserRole === "MANAGER") {
      summary = t("waitingForFighterRequiredDocuments");
    } else if (hasDocumentsWaitingForUpload) {
      summary = t("uploadAllRequiredDocumentsBeforeAcceptingDeal");
    } else if (hasDocumentsPendingReview) {
      summary = t("documentsWaitingForManagerApproval");
    }

    return (
      <div
        id="deal-required-documents-section"
        key="REQUIRED_DOCUMENTS"
        className="grid grid-cols-[96px_minmax(0,1fr)] items-stretch gap-4 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-6"
      >
        <div className="flex h-full min-w-0 flex-col items-center">
          <div
            className={`flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full shrink-0 ${visual.circleBg}`}
          >
            {visual.icon}
          </div>

          <div
            className={`mt-1.5 w-full max-w-[96px] break-words text-center text-base font-normal leading-6 sm:max-w-[104px] ${visual.labelColor}`}
          >
            {getStatusLabel("REQUIRED_DOCUMENTS", t)}
          </div>

          {!isLastStep && (
            <div
              className={`mt-2 w-[2px] flex-1 min-h-[32px] ${visual.lineColor}`}
            />
          )}
        </div>

        <div className="min-w-0 pb-6 sm:pb-8">
          <div className="w-full rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-base font-semibold text-gray-900">
                  {t("requiredDocuments")}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">
                  {summary}
                </p>
              </div>

              <span
                className={`inline-flex w-fit shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${
                  completed
                    ? "border-green-200 bg-green-50 text-green-700"
                    : "border-gray-200 bg-white text-gray-500"
                }`}
              >
                {t("documentsApprovedCount", {
                  approved: approvedDocumentCount,
                  total: requiredDocuments.length,
                })}
              </span>
            </div>

            {currentUserRole === "MANAGER" && (
              <div className="mt-4 space-y-3">
                {requiredDocuments.map(renderManagerDocumentCard)}
              </div>
            )}

            {currentUserRole === "FIGHTER" && !completed && (
              <div className="mt-4">
                {hasDocumentsWaitingForUpload ? (
                  <Button
                    type="button"
                    size="sm"
                    onPress={() => setIsDocumentModalOpen(true)}
                    startContent={<DocumentTextIcon className="h-4 w-4" />}
                    className="h-10 rounded-xl bg-gray-900 px-4 text-sm font-semibold text-white hover:bg-black transition shadow-sm"
                  >
                    {t("uploadDocuments")}
                  </Button>
                ) : (
                  <div className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2 text-sm font-medium text-amber-700">
                    <ClockIcon className="h-4 w-4" />
                    {t("waitingForManagerApproval")}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const agreementMessageKey = getAgreementMessageKey(
    agreement,
    currentUserRole,
  );
  const agreementPdfUrl =
    agreement?.agreementFile?.fileUrl || deal?.agreementFile?.fileUrl;

  const renderAgreementStep = (index: number) => {
    const isLastStep = index === progressSteps.length - 1;
    const signed = isAgreementFullySigned;
    const canSign = canSignAgreement(agreement, currentUserRole);
    const visual = getStepVisualConfig({
      isTerminalFailure: false,
      isDispute: false,
      completed: signed,
      current: !signed,
      isFuture: false,
    });

    const getAgreementSummary = (): string => {
      if (agreementMessageKey) return t(agreementMessageKey);
      if (signed) return t("agreementFullySigned");
      if (currentUserRole === "FIGHTER") return t("agreementWaitingForFighter");
      return t("agreementWaitingForFighterReview");
    };

    return (
      <div
        key="AGREEMENT"
        className="grid grid-cols-[96px_minmax(0,1fr)] items-stretch gap-4 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-6"
      >
        <div className="flex h-full min-w-0 flex-col items-center">
          <div
            className={`flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full shrink-0 ${visual.circleBg}`}
          >
            {visual.icon}
          </div>

          <div
            className={`mt-1.5 w-full max-w-[96px] break-words text-center text-base font-normal leading-6 sm:max-w-[104px] ${visual.labelColor}`}
          >
            {getStatusLabel("AGREEMENT", t)}
          </div>

          {!isLastStep && (
            <div
              className={`mt-2 w-[2px] flex-1 min-h-[32px] ${visual.lineColor}`}
            />
          )}
        </div>

        <div className="min-w-0 pb-6 sm:pb-8">
          <div className="w-full rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-base font-semibold text-gray-900">
                  {getStatusLabel("AGREEMENT", t)}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">
                  {getAgreementSummary()}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setActiveTab("agreement")}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 active:scale-95"
              >
                {canSign
                  ? t("signAgreement")
                  : signed
                    ? t("view")
                    : t("review")}
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {activeTab === "general" && (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6 text-left">
          <h2 className="text-xl font-semibold text-gray-900 font-['Plus_Jakarta_Sans'] leading-7">
            {t("payoutPolicies")}
          </h2>
          <div>
            {formData && renderDynamicObject ? (
              renderDynamicObject(
                (() => {
                  if (!formData || typeof formData !== "object") {
                    return formData;
                  }

                  const {
                    document_tracker,
                    documentRequirement,
                    ...remainingFormData
                  } = formData;

                  return remainingFormData;
                })(),
              )
            ) : (
              <div className="text-sm text-gray-400">{t("noFormData")}</div>
            )}
          </div>
        </div>
      )}

      {activeTab === "progress" && (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6 text-left w-full">
          <h2 className="text-xl font-semibold text-gray-900 font-['Plus_Jakarta_Sans'] leading-7">
            {t("progress")}
          </h2>

          <div className="w-full">
            <div className="space-y-6">
              {progressSteps.map((step, index) => {
                if (step === "REQUIRED_DOCUMENTS") {
                  return renderRequiredDocumentsStep(index);
                }

                if (step === "AGREEMENT") {
                  return renderAgreementStep(index);
                }

                const activities = (deal.activityResponse || []).filter(
                  (activity) => activity.status === step,
                );

                const hasThisStepActivity = activities.length > 0;
                const isStepProcessed = activities.some(
                  (activity) => activity.processed === true,
                );
                const waitingForRequiredDocuments =
                  step === "ACCEPTED" &&
                  hasRequiredDocuments &&
                  !allRequiredDocumentsApproved &&
                  deal.status === "PROPOSED";

                                const completed = isStepProcessed;
                const current =
                  hasThisStepActivity &&
                  !isStepProcessed &&
                  (!waitingForRequiredDocuments || canRespondToDeal);
                const isDispute = step === "DISPUTE";
                const isTerminalFailure =
                  step === "REJECTED" || step === "CANCELLED";
                const hasActivity = activities.length > 0;
                const isFuture =
                  (waitingForRequiredDocuments && !canRespondToDeal) ||
                  (!hasActivity && !isTerminalFailure && !isDispute);
                const isLastStep = index === progressSteps.length - 1;

                const visual = getStepVisualConfig({
                  isTerminalFailure,
                  isDispute,
                  completed,
                  current,
                  isFuture,
                });

                return (
                  <div
                    key={step}
                    style={{
                      opacity: isFuture ? 0.4 : 1,
                      transition: "opacity 0.2s",
                    }}
                    className="grid grid-cols-[96px_minmax(0,1fr)] items-stretch gap-4 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-6"
                  >
                    <div className="flex h-full min-w-0 flex-col items-center">
                      <div
                        className={`flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full shrink-0 ${visual.circleBg}`}
                      >
                        {visual.icon}
                      </div>

                      <div
                        className={`mt-1.5 w-full max-w-[96px] break-words text-center text-base font-normal leading-6 sm:max-w-[104px] ${visual.labelColor}`}
                      >
                        {getStatusLabel(step, t)}
                      </div>

                      {!isLastStep && (
                        <div
                          className={`mt-2 w-[2px] flex-1 min-h-[32px] ${visual.lineColor}`}
                        />
                      )}
                    </div>

                    <div className="min-w-0 pb-6 sm:pb-8">
                      {activities.length > 0 ? (
                        <div className="space-y-3">
                          {activities.map((activity) => {
                            const isPendingDealAcceptance =
                              activity.status === "ACCEPTED" &&
                              activity.processed !== true;
                            const shouldRenderDealResponseHere =
                              canRespondToDeal &&
                              isPendingDealAcceptance &&
                              !shouldHideProgressActions;
                                                        const shouldShowDealResponseButtons =
                              shouldRenderDealResponseHere;
                            const shouldShowAcceptButton =
                              shouldRenderDealResponseHere &&
                              documentsReadyForAcceptance;
                            const canShowDefaultActivityButton =
                              !shouldHideProgressActions &&
                              !shouldShowDealResponseButtons;
                            const actionButton = canShowDefaultActivityButton
                              ? getActivityActionButton(
                                  activity,
                                  currentUserRole,
                                  deal.activityResponse || [],
                                )
                              : null;

                            return (
                              <div
                                key={activity.id}
                                id={
                                  isPendingDealAcceptance
                                    ? "deal-progress-accepted-action"
                                    : undefined
                                }
                                className={`w-full rounded-2xl border p-4 font-['Plus_Jakarta_Sans'] ${
                                  isDispute || isTerminalFailure
                                    ? "border-red-200 bg-red-50"
                                    : "border-gray-200 bg-white"
                                }`}
                              >
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center justify-between px-3 py-1.5 rounded-[20px]">
                                    <div className="flex items-center gap-3">
                                      <Avatar
                                        size="sm"
                                        src={activity.performedBy?.profileImage}
                                        name={`${activity.performedBy?.firstName?.[0] || ""}${activity.performedBy?.lastName?.[0] || ""}`}
                                        className="shrink-0"
                                      />
                                      <div className="flex flex-col">
                                        <p
                                          className="text-sm font-medium text-gray-900 cursor-pointer hover:underline leading-5"
                                          onClick={() => {
                                            if (activity.performedBy?.id) {
                                              navigate(`/profile?profileId=${activity.performedBy.id}`);
                                            }
                                          }}
                                        >
                                          {activity.performedBy
                                            ? `${activity.performedBy.firstName} ${activity.performedBy.lastName}`
                                            : t("systemUser")}
                                        </p>
                                      </div>
                                    </div>

                                    {actionButton && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (shouldDisableProgressActions) return;
                                          if (shouldSwitchToComponentPayments(activity)) {
                                            setActiveTab("componentPayments");
                                            return;
                                          }
                                          switchTabAndScroll(actionButton.tab, actionButton.scrollTargetId);
                                        }}
                                        className={[
                                          "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition",
                                          shouldDisableProgressActions
                                            ? "cursor-not-allowed opacity-50"
                                            : "hover:bg-gray-50 active:scale-95",
                                        ].join(" ")}
                                        disabled={shouldDisableProgressActions}
                                      >
                                        {t(actionButton.labelKey)}
                                        <ArrowRightIcon className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>

                                  <div className="pl-14 flex flex-col gap-1">
                                    {shouldRenderDealResponseHere && !documentsReadyForAcceptance && (
                                      <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-100 px-3 py-2 text-xs font-medium text-gray-500">
                                        <DocumentTextIcon className="h-4 w-4" />
                                        {t("completeRequiredDocumentsBeforeAccepting")}
                                      </div>
                                    )}

                                    <p
                                      className="text-base font-normal leading-6 text-gray-900 break-words"
                                      dangerouslySetInnerHTML={{ __html: getActivityDisplayMessage(activity) }}
                                    />

                                    {shouldShowDealResponseButtons && (
                                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                                        {onDeclineDeal && (
                                          <button
                                            type="button"
                                            onClick={() => { void onDeclineDeal(); }}
                                            disabled={isDealActionProcessing}
                                            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-3xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                          >
                                            <XMarkIcon className="h-4 w-4" />
                                            {isDealActionProcessing ? t("processing") : t("declineDeal")}
                                          </button>
                                        )}
                                        {onAcceptDeal && shouldShowAcceptButton && (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() => { void onAcceptDeal(); }}
                                               disabled={
                                                isDealActionProcessing ||
                                                !canAcceptDeal(agreement)
                                              }
                                              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-3xl bg-green-600 px-4 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                              <CheckIcon className="h-4 w-4" />
                                              {isDealActionProcessing ? t("processing") : t("acceptDeal")}
                                            </button>
                                            {!canAcceptDeal(agreement) && (
                                              <p className="text-sm text-gray-500">
                                                {t("agreementRequiredToAccept")}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}

                                    <span className="block text-base font-normal text-gray-500 leading-6">
                                      {new Date(activity.createdAt + "Z").toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="pt-4 text-sm italic text-gray-400">
                          {isTerminalFailure && current
                            ? t("dealRejected")
                            : t("noActivityForStep")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === "agreement" && (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6 text-left w-full">
          <h2 className="text-xl font-semibold text-gray-900 font-['Plus_Jakarta_Sans'] leading-7">
            {t("agreement")}
          </h2>

          {agreement ? (
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-4">
              {canSignAgreement(agreement, currentUserRole) ? (
                <>
                  {agreementMessageKey && (
                    <p className="text-sm text-gray-500">{t(agreementMessageKey)}</p>
                  )}
                  <AgreementSignPanel
                    pdfUrl={agreementPdfUrl}
                    isSubmitting={isDealActionProcessing}
                    submitLabel={t("signAgreement")}
                    onSign={(dataUrl: string) => {
                      if (onSignAgreement) void onSignAgreement(dataUrl);
                    }}
                  />
                </>
              ) : (
                <>
                  {agreementMessageKey && (
                    <p className="text-sm text-gray-500">{t(agreementMessageKey)}</p>
                  )}
                  {agreement.status === "VOID" && agreement.voidReason && (
                    <p className="text-sm text-gray-500">{agreement.voidReason}</p>
                  )}
                  {agreementPdfUrl ? (
                    <AgreementPdfViewer
                      pdfUrl={agreementPdfUrl}
                      title={t("agreement")}
                      emptyMessage={t("agreementNotReady")}
                    />
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t("agreementNotReady")}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("agreementNotReady")}</p>
          )}
        </div>
      )}

      {activeTab === "activity" && (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6 text-left">
          <h2 className="text-xl font-semibold text-gray-900 font-['Plus_Jakarta_Sans'] leading-7">
            {t("activity")}
          </h2>
          <div className="space-y-4">
          {!deal.activityResponse || deal.activityResponse.length === 0 ? (
            <p className="text-sm text-gray-500">{t("noActivityYet")}</p>
          ) : (
            <div className="divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-200">
              {deal.activityResponse.map((activity) => {
                const hasUser = !!activity.performedBy;
                const actorName = hasUser
                  ? `${activity.performedBy?.firstName || ""} ${
                      activity.performedBy?.lastName || ""
                    }`.trim()
                  : t("systemUser");
                const actorInitials = hasUser
                  ? `${activity.performedBy?.firstName?.[0] || ""}${
                      activity.performedBy?.lastName?.[0] || ""
                    }`.trim() || "U"
                  : "S";
                const avatarSrc = hasUser
                  ? activity.performedBy?.profileImage
                  : undefined;

                return (
                  <div
                    key={activity.id}
                    className="bg-white px-4 py-4 transition-colors hover:bg-gray-50"
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0">
                        {hasUser ? (
                          <Avatar
                            size="sm"
                            src={avatarSrc}
                            name={actorInitials}
                            className="shrink-0"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-700">
                            {actorInitials}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <span
                                className={`text-sm font-medium ${
                                  hasUser
                                    ? "cursor-pointer text-gray-900 hover:underline"
                                    : "text-gray-500"
                                }`}
                                onClick={() => {
                                  if (activity.performedBy?.id) {
                                    navigate(
                                      `/profile?profileId=${activity.performedBy.id}`,
                                    );
                                  }
                                }}
                              >
                                {actorName}
                              </span>
                            </div>

                            <div
                              className="break-words text-base font-normal leading-6 text-gray-700"
                              dangerouslySetInnerHTML={{
                                __html: getActivityDisplayMessage(activity),
                              }}
                            />
                          </div>

                          <Chip
                            size="sm"
                            color="primary"
                            variant="flat"
                            className="shrink-0 text-[10px] uppercase"
                          >
                            {getActivityStatusLabel(activity.status, t)}
                          </Chip>
                        </div>

                        <span className="mt-1 block text-base font-normal leading-6 text-gray-500">
                          {new Date(activity.createdAt + "Z").toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      )}

      {currentUserRole === "FIGHTER" && (
        <Modal
          isOpen={isDocumentModalOpen}
          onClose={() => setIsDocumentModalOpen(false)}
          placement="center"
          scrollBehavior="inside"
          size="2xl"
          radius="lg"
          classNames={{
            backdrop: "bg-black/45 backdrop-blur-[1px]",
            base: "max-h-[88vh] rounded-2xl border border-gray-200 bg-white shadow-2xl",
            body: "min-h-0 overscroll-contain rounded-b-2xl",
            closeButton:
              "right-4 top-4 text-gray-500 hover:bg-gray-100 hover:text-gray-800",
          }}
        >
          <ModalContent>
            <ModalHeader className="border-b border-gray-200 px-5 py-4 sm:px-6">
              <div className="flex min-w-0 items-start gap-3 pr-10">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <DocumentTextIcon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h2
                    id="required-documents-modal-title"
                    className="text-sm font-semibold leading-5 text-gray-900"
                  >
                    {t("requiredDocuments")}
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">
                    {t("uploadRequestedDocumentsForDeal")}
                  </p>
                </div>
              </div>
            </ModalHeader>

            <ModalBody
              className="space-y-4 bg-gray-50/60 p-5 text-sm text-gray-700 sm:p-6"
              style={{ overflowAnchor: "none" }}
            >
              {requiredDocuments.map((document, index) => {
                const documentKey = `fighter-document-${index}`;
                const status = normalizeDocumentStatus(document.uploadStatus);
                const isRejected = status === "REJECTED";
                const isPending = status === "PENDING";
                const deadlinePassed =
                  isRejected && isPastDeadline(document.deadlineDate);
                const canUpload =
                  deal.status === "PROPOSED" &&
                  (isPending || (isRejected && !deadlinePassed));
                const isUploading = uploadingDocumentKey === documentKey;
                const documentName = document.documentType;

                return (
                  <div
                    key={documentKey}
                    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDocumentName(documentName)}
                        </p>
                        {document.deadlineDate && (
                          <p className="mt-1 text-xs text-gray-500">
                            {t("documentDeadline", { date: document.deadlineDate })}
                          </p>
                        )}

                        {document.notes && (
                          <p className="mt-1 text-xs leading-5 text-gray-500">
                            {document.notes}
                          </p>
                        )}
                      </div>

                      <span
                        className={`inline-flex w-fit shrink-0 self-start rounded-full border px-2.5 py-1 text-xs font-medium ${getDocumentStatusClasses(
                          status,
                        )}`}
                      >
                        {getDocumentStatusText(status, t)}
                      </span>
                    </div>

                    {document.uploadedFile && status !== "REJECTED" && (
                      <PdfFileBadge
                        fileName={getFileNameFromUrl(document.uploadedFile)}
                        className="mt-3"
                      />
                    )}

                    {isRejected && document.reviewComment && (
                      <div className="mt-3 rounded-md border border-red-100 bg-red-50 p-3">
                        <p className="text-xs font-medium text-red-700">
                          {t("reason")}
                        </p>
                        <p className="mt-1 text-sm text-red-700">
                          {document.reviewComment}
                        </p>
                      </div>
                    )}

                    {deadlinePassed && (
                      <p className="mt-3 text-sm font-medium text-red-600">
                        {t("reuploadDeadlinePassed")}
                      </p>
                    )}

                    {canUpload && (
                      <div className="mt-4 space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <input
                            id={`${documentKey}-file`}
                            type="file"
                            accept="application/pdf,.pdf"
                            onClick={(event) => {
                              (event.target as HTMLInputElement).value = "";
                            }}
                            onChange={(event) =>
                              handleFileChange(event, documentKey)
                            }
                            className="hidden"
                          />
                          <label
                            htmlFor={`${documentKey}-file`}
                            className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-gray-200 px-3 text-sm font-medium text-gray-700 transition hover:bg-gray-300"
                          >
                            {t("chooseFile")}
                          </label>
                          {!selectedFiles[documentKey] && (
                            <span className="text-sm text-gray-500">
                              {t("noFileChosen")}
                            </span>
                          )}
                        </div>

                        {selectedFiles[documentKey] && (
                          <PdfFileBadge
                            fileName={
                              selectedFiles[documentKey]?.name || "document.pdf"
                            }
                            className="bg-gray-50"
                          />
                        )}

                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onPress={() =>
                              void handleUploadDocument(document, documentKey)
                            }
                            isDisabled={
                              !selectedFiles[documentKey] ||
                              isUploading ||
                              !onUploadRequiredDocument
                            }
                            isLoading={isUploading}
                            className="h-9 rounded-md bg-gray-900 px-4 text-sm font-semibold text-white data-[hover=true]:bg-gray-800"
                          >
                            {isUploading
                              ? t("uploadingFile")
                              : isRejected
                                ? t("uploadAgain")
                                : t("uploadButton")}
                          </Button>

                          {selectedFiles[documentKey] && (
                            <Button
                              type="button"
                              size="sm"
                              variant="bordered"
                              onPress={() =>
                                setSelectedFiles((previous) => ({
                                  ...previous,
                                  [documentKey]: null,
                                }))
                              }
                              isDisabled={isUploading}
                              className="h-9 rounded-md border-gray-300 bg-white px-4 text-sm font-medium text-gray-700"
                            >
                              {t("remove")}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {status === "PENDING_REVIEW" && (
                      <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-amber-700">
                        <ClockIcon className="h-4 w-4" />
                        {t("waitingForManagerApproval")}
                      </div>
                    )}

                    {status === "APPROVED" && (
                      <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-green-700">
                        <CheckCircleIcon className="h-4 w-4" />
                        {t("documentApproved")}
                      </div>
                    )}

                    {documentErrors[documentKey] && (
                      <p className="mt-2 text-xs font-medium text-red-600">
                        {documentErrors[documentKey]}
                      </p>
                    )}
                  </div>
                );
              })}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default DealActivityTab;